import React from 'react';
import { PageHeader } from '../components/PageBits';
import { ASSET } from '../mock/data';
import { ArrowRight } from 'lucide-react';
import { GraduationCap, Users, Search, BookOpen, Heart, Award } from 'lucide-react';

const howItWorks = [
  { icon: Users, text: 'Students are identified through local community networks and REFOCC chapters.' },
  { icon: Search, text: 'Applications or nominations are reviewed with care and responsibility.' },
  { icon: GraduationCap, text: 'Selected students receive educational support based on need and eligibility.' },
  { icon: Heart, text: 'The programme encourages students to continue learning and give back to society.' },
];

const Scholarship = () => (
  <>
    <PageHeader kicker="Scholarship Programme" title="Education should never stop" accent="at a financial barrier." intro="REFOCC's scholarship initiative is built on the belief that deserving students must be encouraged, supported, and given the confidence to continue their education. The programme helps transform community goodwill into real academic opportunity." />
    <section className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <img src={`${ASSET}/gallery-11.jpg`} alt="Scholarship" className="w-full rounded-[2rem] aspect-[16/7] object-cover"/>
      </div>
    </section>

    {/* Programme Overview */}
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-3xl md:text-4xl text-refocc-dark mb-6">Programme Overview</h2>
        <p className="text-refocc-dark/70 leading-relaxed max-w-3xl">Through chapter-level recommendations and community review, REFOCC identifies students who need support and encouragement. The scholarship programme is designed not only as financial assistance, but also as a message of trust: the community believes in the student's future.</p>
      </div>
    </section>

    {/* How It Works */}
    <section className="py-16 bg-refocc-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-3xl md:text-4xl text-refocc-dark mb-10 text-center">How it works</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {howItWorks.map((step, i) => (
            <div key={i} className="flex gap-5 bg-white rounded-2xl p-6 border border-refocc-dark/10">
              <div className="w-12 h-12 rounded-full bg-refocc-orange/15 text-refocc-orange flex items-center justify-center shrink-0">
                <step.icon size={20}/>
              </div>
              <p className="text-refocc-dark/70 leading-relaxed">{step.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center"><a href="http://scholarships.refocc.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-refocc-orange text-white px-6 py-3 rounded-full font-medium hover:bg-[#B97651] transition-colors">Apply for Scholarship <ArrowRight size={16}/></a></div>
      </div>
    </section>

    {/* Eligibility */}
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-serif text-3xl md:text-4xl text-refocc-dark text-center mb-10">Eligibility Direction</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: 'Need-based priority', d: 'For students and families facing financial hardship.' },
            { t: 'Academic commitment', d: 'Willingness to continue education seriously and maintain consistent effort.' },
            { t: 'Community spirit', d: 'A positive attitude toward helping others and a spirit of giving back.' }
          ].map(x => (
            <div key={x.t} className="bg-white rounded-2xl p-6 border border-refocc-dark/10">
              <div className="font-serif text-xl text-refocc-dark">{x.t}</div>
              <p className="text-sm text-refocc-dark/65 mt-2 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Dyuthi Highlight */}
    <section className="py-16 bg-refocc-dark text-refocc-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-refocc-orange/20 text-refocc-orange px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
              <Award size={14} /> Dyuthi — 10th Anniversary
            </div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-6">REFOCC Decennial Scholars Award</h2>
            <p className="text-refocc-cream/75 leading-relaxed mb-4">This landmark scholarship supports 10 deserving students over four years and presents a one-time cash award of ₹5,000 each to 40 students.</p>
            <p className="text-refocc-cream/60 leading-relaxed">Dyuthi reflects REFOCC's commitment to social responsibility, educational empowerment, and the belief that every student deserves a chance to rise.</p>
          </div>
          <div className="grid gap-4">
            <img src={`${ASSET}/dyuthi-scholars-award.jpg`} alt="REFOCC Decennial Scholars Award" className="w-full rounded-2xl shadow-xl aspect-[16/10] object-cover" />
            <img src={`${ASSET}/dyuthi-brochure.jpg`} alt="Dyuthi Brochure" className="w-full rounded-2xl shadow-xl aspect-[16/10] object-cover" />
          </div>
        </div>
      </div>
    </section>
  </>
);

export default Scholarship;
